import { useSummerSaleBanner } from '@/hooks/useSummerSaleBanner';
import { SUMMER_SALE } from '@/lib/constants';
import { useIsTabletOrWeb } from '@/lib/useDeviceType';
import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Image,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Mirrors the WordPress "Summer Sale Site Banner" Kadence conversions: a
// light-yellow band with the Summer Sale logo + blue italic underlined copy
// (minus the WP versions' black strip, which read as dead space in the app).
// The 'countdown' variant mirrors WP post 1601603 ("Final 72 Hours Timer"):
// ticking DAYS:HRS:MIN:SEC clock, "Your Last Chance…" copy, logo on the right,
// stacked vertically on narrow screens.
const COLORS = {
  band: '#ffff92',
  text: '#003399',
  clockBox: '#1A0A00',
  clockDigits: '#F6DA94',
  clockSeconds: '#F2663A',
} as const;

// Sam's Summer Sale logo (same asset the WP banners use). Intrinsic 1045×400.
const LOGO_URL =
  'https://www.openstudiojazz.com/wp-content/uploads/2026/07/summer-sitebanner.png';
const LOGO_ASPECT_RATIO = 1045 / 400;
const LOGO_HEIGHT = 40;

const CLASSIC_LABEL = "Summer Sale — Don't Miss Our Best Price of the Year →";
const COUNTDOWN_LABEL = 'Summer Sale — Your Last Chance: Get the Best Price of the Year →';

// The WP design stacks the banner (clock / copy / logo) at ≤480px CSS width.
const STACKED_MAX_WIDTH = 480;

function openSaleUrl(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    void Linking.openURL(url);
  }
}

function pad(n: number): string {
  return String(Math.max(0, n)).padStart(2, '0');
}

function remainingSeconds(): number {
  return Math.max(0, Math.floor((SUMMER_SALE.END_MS - Date.now()) / 1000));
}

function ClockUnit({
  value,
  label,
  compact,
  isSeconds,
  opacity,
}: {
  value: number;
  label: string;
  compact: boolean;
  isSeconds?: boolean;
  opacity?: Animated.Value;
}) {
  return (
    <View style={clockStyles.unit}>
      <Animated.Text
        style={[
          clockStyles.num,
          compact && clockStyles.numCompact,
          isSeconds && clockStyles.numSeconds,
          opacity != null && { opacity },
        ]}
      >
        {pad(value)}
      </Animated.Text>
      <Text style={[clockStyles.label, compact && clockStyles.labelCompact]}>{label}</Text>
    </View>
  );
}

function ClockColon({ compact }: { compact: boolean }) {
  return (
    <Text
      style={[clockStyles.colon, compact && clockStyles.colonCompact]}
      accessibilityElementsHidden
    >
      :
    </Text>
  );
}

/** DAYS:HRS:MIN:SEC countdown to the sale end, ticking every second. */
function CountdownClock({ compact }: { compact: boolean }) {
  const [remaining, setRemaining] = useState(remainingSeconds);
  const secondsOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const id = setInterval(() => setRemaining(remainingSeconds()), 1000);
    return () => clearInterval(id);
  }, []);

  const secs = remaining % 60;

  // Mirrors the WP banner's .15s "ticking" fade on the seconds digits.
  useEffect(() => {
    secondsOpacity.setValue(0.5);
    Animated.timing(secondsOpacity, { toValue: 1, duration: 150, useNativeDriver: true }).start();
  }, [secs, secondsOpacity]);

  return (
    <View
      style={[clockStyles.clock, compact && clockStyles.clockCompact]}
      accessibilityRole="timer"
      accessibilityLabel="Sale countdown"
    >
      <ClockUnit value={Math.floor(remaining / 86400)} label="days" compact={compact} />
      <ClockColon compact={compact} />
      <ClockUnit value={Math.floor((remaining % 86400) / 3600)} label="hrs" compact={compact} />
      <ClockColon compact={compact} />
      <ClockUnit value={Math.floor((remaining % 3600) / 60)} label="min" compact={compact} />
      <ClockColon compact={compact} />
      <ClockUnit value={secs} label="sec" compact={compact} isSeconds opacity={secondsOpacity} />
    </View>
  );
}

/**
 * Sale banner shown on every authenticated page for the duration of the sale
 * (audience rules and the classic/countdown variant flip live in
 * useSummerSaleBanner). Renders nothing when the gate isn't met, so it's safe
 * to mount unconditionally. The X dismisses it for the rest of the day.
 */
export function SummerSaleBanner() {
  const banner = useSummerSaleBanner();
  const isTabletOrWeb = useIsTabletOrWeb();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();

  // Web-only: avoids App Store anti-steering issues from linking to an external
  // web purchase page inside the native iOS/Android app.
  if (Platform.OS !== 'web' || !banner) return null;

  const isCountdown = banner.variant === 'countdown';
  const stacked = isCountdown && width <= STACKED_MAX_WIDTH;
  const fontSize = isTabletOrWeb ? 18 : 16;

  return (
    <View style={{ backgroundColor: COLORS.band, paddingTop: insets.top }}>
      <Pressable
        onPress={() => openSaleUrl(banner.url)}
        accessibilityRole="link"
        accessibilityLabel={isCountdown ? COUNTDOWN_LABEL : CLASSIC_LABEL}
        style={[styles.band, isCountdown && styles.bandCountdown, stacked && styles.bandStacked]}
      >
        {isCountdown ? (
          <>
            <CountdownClock compact={stacked} />
            <View style={styles.countdownCopy}>
              <Text style={[styles.countdownText, { fontSize }]}>Your Last Chance...</Text>
              <Text style={[styles.countdownText, styles.link, { fontSize }]}>
                Get the Best Price of the Year →
              </Text>
            </View>
            <Image
              source={{ uri: LOGO_URL }}
              style={[styles.countdownLogo, stacked && styles.countdownLogoStacked]}
              resizeMode="contain"
              accessibilityLabel="Summer Sale"
            />
          </>
        ) : (
          <>
            <Image
              source={{ uri: LOGO_URL }}
              style={styles.logo}
              resizeMode="contain"
              accessibilityLabel="Summer Sale"
            />
            <Text style={[styles.text, styles.link, { fontSize }]} numberOfLines={2}>
              Don&apos;t Miss Our Best Price of the Year →
            </Text>
          </>
        )}
      </Pressable>
      <Pressable
        onPress={banner.dismiss}
        accessibilityRole="button"
        accessibilityLabel="Dismiss Summer Sale banner"
        hitSlop={8}
        style={[styles.closeButton, { top: insets.top }]}
      >
        <Ionicons name="close" size={20} color={COLORS.text} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  band: {
    backgroundColor: COLORS.band,
    paddingVertical: 10,
    // Extra right padding keeps the centered copy clear of the close button.
    paddingLeft: 16,
    paddingRight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  bandCountdown: {
    gap: 14,
  },
  bandStacked: {
    flexDirection: 'column',
    gap: 4,
    paddingHorizontal: 16,
  },
  countdownCopy: {
    flexShrink: 1,
    alignItems: 'center',
  },
  countdownText: {
    color: COLORS.text,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 21,
  },
  countdownLogo: {
    height: 34,
    aspectRatio: LOGO_ASPECT_RATIO,
    flexShrink: 0,
  },
  countdownLogoStacked: {
    height: 23,
    marginTop: 4,
  },
  logo: {
    height: LOGO_HEIGHT,
    aspectRatio: LOGO_ASPECT_RATIO,
    flexShrink: 0,
  },
  text: {
    color: COLORS.text,
    fontStyle: 'italic',
    fontWeight: '700',
    textAlign: 'center',
  },
  link: {
    color: COLORS.text,
    textDecorationLine: 'underline',
  },
  closeButton: {
    position: 'absolute',
    right: 10,
    bottom: 0,
    justifyContent: 'center',
  },
});

const CLOCK_MONO_FONT = "'Courier New', Courier, monospace";

const clockStyles = StyleSheet.create({
  clock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
  },
  clockCompact: {
    gap: 2,
  },
  unit: {
    alignItems: 'center',
    gap: 1,
  },
  num: {
    backgroundColor: COLORS.clockBox,
    color: COLORS.clockDigits,
    fontSize: 17,
    fontWeight: '700',
    fontFamily: CLOCK_MONO_FONT,
    width: 31,
    height: 31,
    lineHeight: 31,
    textAlign: 'center',
    borderRadius: 5,
    overflow: 'hidden',
  },
  numCompact: {
    fontSize: 14,
    width: 27,
    height: 27,
    lineHeight: 27,
    borderRadius: 4,
  },
  numSeconds: {
    color: COLORS.clockSeconds,
  },
  label: {
    fontSize: 8,
    fontWeight: '700',
    letterSpacing: 0.64,
    textTransform: 'uppercase',
    color: COLORS.text,
    opacity: 0.85,
  },
  labelCompact: {
    fontSize: 7,
  },
  colon: {
    fontSize: 17,
    fontWeight: '700',
    color: COLORS.text,
    opacity: 0.7,
    paddingBottom: 12,
    lineHeight: 17,
  },
  colonCompact: {
    fontSize: 14,
    lineHeight: 14,
    paddingBottom: 9,
  },
});
